import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box, Chip, Link, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Button } from '../components/atoms/Button'
import { Text } from '../components/atoms/Text'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { downloadFile } from '../utils/download'

interface Partner {
  id: number
  name: string
  notes: string | null
  users: number
}

interface PartnerUser {
  id: number
  email: string
  name: string | null
  partnerId: number | null
  hwIds?: string[]
}

interface PartnerQuarter {
  year: number
  quarter: number
  label: string
  status: 'draft' | 'locked' | 'recorded'
  customers: number
}

interface MatchLine {
  line: string
  kind: 'email' | 'hw_id' | null
  key: string | null
  user: PartnerUser | null
}

// Partners that pay their customers' reserve compensation themselves (Rasol): the payout run skips their users.
export function AdminPayoutPartnersPage(): React.ReactElement {
  const { t } = useTranslation()
  const app = useApp()

  const [partners, setPartners] = useState<Partner[]>([])
  const [partnerId, setPartnerId] = useState<number | null>(null)
  const [users, setUsers] = useState<PartnerUser[]>([])
  const [quarters, setQuarters] = useState<PartnerQuarter[]>([])
  const [newName, setNewName] = useState('')
  const [text, setText] = useState('')
  const [lines, setLines] = useState<MatchLine[] | null>(null)
  const [busy, setBusy] = useState(false)

  const partner = partners.find(p => p.id === partnerId)
  const partnerName = (id: number | null) => partners.find(p => p.id === id)?.name || '-'

  const loadPartners = (select?: number) => {
    callAxios<{ partners: Partner[] }>({ url: '/admin/payout-partners' }).then(res => {
      if (!res.success) return app.addError(res)
      setPartners(res.data.partners)
      setPartnerId(id => select ?? id ?? res.data.partners[0]?.id ?? null)
    })
  }

  const loadUsers = (id: number) => {
    callAxios<{ users: PartnerUser[] }>({ url: `/admin/payout-partners/${id}/users` }).then(res => {
      if (res.success) setUsers(res.data.users)
      else app.addError(res)
    })
  }

  const loadQuarters = (id: number) => {
    callAxios<{ quarters: PartnerQuarter[] }>({ url: `/admin/payout-partners/${id}/quarters` }).then(res => {
      if (res.success) setQuarters(res.data.quarters)
      else app.addError(res)
    })
  }

  useEffect(() => loadPartners(), []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLines(null)
    setQuarters([])
    if (partnerId) {
      loadUsers(partnerId)
      loadQuarters(partnerId)
    }
  }, [partnerId]) // eslint-disable-line react-hooks/exhaustive-deps

  // What the partner invoices us by: the summary and a page per customer of the quarter's run.
  const downloadPdf = (q: PartnerQuarter) => {
    if (!partner) return
    const name = partner.name.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '')
    downloadFile(`/admin/payout-partners/${partner.id}/${q.year}/${q.quarter}/pdf`, `${name}-${q.label}.pdf`)
      .then(ok => { if (!ok) app.addError(t('Download failed.')) })
  }

  const create = () => {
    callAxios<{ partner: Partner }>({ method: 'POST', url: '/admin/payout-partners' }, { name: newName }).then(res => {
      if (!res.success) return app.addError(res)
      setNewName('')
      loadPartners(res.data.partner.id)
    })
  }

  const check = () => {
    setBusy(true)
    callAxios<{ lines: MatchLine[] }>({ method: 'POST', url: '/admin/payout-partners/match' }, { text }).then(res => {
      setBusy(false)
      if (res.success) setLines(res.data.lines)
      else app.addError(res)
    })
  }

  // The users to add: found, not in this partner yet, each once.
  const toAdd = Array.from(new Set((lines || [])
    .filter(l => l.user && l.user.partnerId !== partnerId)
    .map(l => (l.user as PartnerUser).id)))

  const add = () => {
    if (!partnerId) return
    setBusy(true)
    callAxios<{ added: number }>({ method: 'POST', url: `/admin/payout-partners/${partnerId}/users` }, { user_ids: toAdd })
      .then(res => {
        setBusy(false)
        if (!res.success) return app.addError(res)
        app.addInfo(`${res.data.added} ${t('users added to')} ${partner?.name}`)
        setText('')
        setLines(null)
        loadPartners()
        loadUsers(partnerId)
      })
  }

  const remove = (user: PartnerUser) => {
    if (!partnerId) return
    callAxios({ method: 'DELETE', url: `/admin/payout-partners/${partnerId}/users/${user.id}` }).then(res => {
      if (!res.success) return app.addError(res)
      app.addInfo(`${user.email}: ${t('removed from')} ${partner?.name}`)
      loadPartners()
      loadUsers(partnerId)
    })
  }

  const lineStatus = (l: MatchLine): [string, 'success' | 'default' | 'warning' | 'error'] => {
    if (!l.kind) return [t('no email or device - ignored'), 'default']
    if (!l.user) return [l.kind === 'email' ? t('no such user') : t('no such device or no owner'), 'error']
    if (l.user.partnerId === partnerId) return [t('already here'), 'default']
    if (l.user.partnerId) return [`${t('moves from')} ${partnerName(l.user.partnerId)}`, 'warning']
    return [t('to be added'), 'success']
  }

  return (
    <Box>
      <PageTitle title={t('Menu-Payout Partners')}/>
      <Box sx={{ mb: 2, opacity: 0.8 }}>
        {t('Partners pay the reserve compensation of their customers themselves; the quarterly payout run skips their users.')}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 3 }}>
        <Select
          size="small"
          data-cy='select-partner'
          value={partnerId ?? ''}
          onChange={e => setPartnerId(Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          {partners.map(p => <MenuItem key={p.id} value={p.id}>{`${p.name} (${p.users})`}</MenuItem>)}
        </Select>
        <Text label={t('New partner')} value={newName} onChange={e => setNewName(e.target.value)}/>
        <Button onClick={create} disabled={!newName.trim()}><Trans>Add</Trans></Button>
      </Stack>

      {partner && <>
        <PageSubtitle title={`${t('Add users to')} ${partner.name}`}/>
        <Box sx={{ my: 1, fontSize: '0.9em', opacity: 0.8 }}>
          {t('Paste the list from the partner: on each line the email address, or else the 8-character device id, is used; names, dates and other text are ignored.')}
        </Box>
        <Text
          data-cy='input-partner-list'
          label={t('List')}
          value={text}
          onChange={e => { setText(e.target.value); setLines(null) }}
          multiline
          minRows={4}
          fullWidth
        />
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <Button data-cy='button-Check' onClick={check} disabled={busy || !text.trim()}><Trans>Check</Trans></Button>
          {lines &&
            <Button data-cy='button-Add users' onClick={add} disabled={busy || toAdd.length === 0}>
              {`${t('Add')} ${toAdd.length} ${t('users to')} ${partner.name}`}
            </Button>}
        </Stack>

        {lines &&
          <TableContainer sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Trans>Line</Trans></TableCell>
                  <TableCell><Trans>Found</Trans></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, i) => {
                  const [label, color] = lineStatus(l)
                  return (
                    <TableRow key={i} data-cy={`match-${i}`}>
                      <TableCell sx={{ width: '55%', overflowWrap: 'anywhere', opacity: l.kind ? 1 : 0.5 }}>
                        {l.line}
                        {l.key && <Box sx={{ fontFamily: 'monospace', fontSize: '0.85em', opacity: 0.7 }}>{l.key}</Box>}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        <Chip size="small" color={color} label={label}/>
                        {l.user && <Box sx={{ fontSize: '0.85em', mt: 0.5 }}>{`${l.user.email} · ${l.user.name || '-'}`}</Box>}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>}

        <PageSubtitle title={`${t('Statements of')} ${partner.name}`}/>
        <Box sx={{ my: 1, fontSize: '0.9em', opacity: 0.8 }}>
          {t('The breakdown the partner invoices us by: the accounting rows and the total, then a page per customer. With VAT 25.5 %; not a self-billed invoice.')}
        </Box>
        {quarters.length === 0
          ? <Box sx={{ mb: 4, opacity: 0.6 }} data-cy='partner-quarters-none'>{t('No customers of the partner in any payout run yet.')}</Box>
          : <TableContainer sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Trans>Quarter</Trans></TableCell>
                  <TableCell><Trans>Status</Trans></TableCell>
                  <TableCell align="right"><Trans>Customers</Trans></TableCell>
                  <TableCell/>
                </TableRow>
              </TableHead>
              <TableBody>
                {quarters.map(q =>
                  <TableRow key={q.label} data-cy={`partner-quarter-${q.label}`}>
                    <TableCell>{`Q${q.quarter}/${q.year}`}</TableCell>
                    <TableCell>{t(`PayoutStatus-${q.status}`)}</TableCell>
                    <TableCell align="right">{q.customers}</TableCell>
                    <TableCell align="right">
                      <Button data-cy={`partner-pdf-${q.label}`} onClick={() => downloadPdf(q)}>PDF</Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>}

        <PageSubtitle title={`${t('Users of')} ${partner.name} (${users.length})`}/>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Trans>Email</Trans></TableCell>
                <TableCell><Trans>Name</Trans></TableCell>
                <TableCell><Trans>Devices</Trans></TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u =>
                <TableRow key={u.id} data-cy={`partner-user-${u.id}`}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.name || '-'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{(u.hwIds || []).join(', ')}</TableCell>
                  <TableCell>
                    <Link component="button" data-cy={`remove-${u.id}`} onClick={() => remove(u)}>{t('Remove')}</Link>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>}
    </Box>
  )
}
